/**
 * Pose Library sidebar panel — save, browse, apply, and manage poses.
 *
 * Renders as a glass card inside the PoseSidebar, matching the visual
 * conventions of MannequinMorphPanel and other sidebar sections.
 *
 * Shows built-in default poses (non-deletable, non-renameable) followed
 * by user-created cloud library poses.
 *
 * Row interaction:
 * - Tapping a row opens a confirm-apply popover.
 * - A three-dots menu provides Rename, Overwrite, Duplicate, and Delete.
 */

import { Check, Plus, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyDefaultPose,
  DEFAULT_POSES,
  type DefaultPose,
} from "../app/defaultPoses.ts";
import { useLibraryStore } from "../app/libraryStore.ts";
import {
  duplicateLibraryItem,
  loadPoseFromLibrary,
  overwritePoseInLibrary,
  savePoseToLibrary,
} from "../app/poseLibrary.ts";
import { Button, IconButton } from "./Button.tsx";
import { showConfirmDialog } from "./ConfirmDialog.tsx";
import { LibraryItemRow } from "./LibraryItemRow.tsx";
import { Popover } from "./Popover.tsx";
import { SearchField } from "./shared.tsx";
import { PANEL_INPUT_STYLE } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_BORDER_STRONG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_SM,
} from "./tokens.ts";

export function PoseLibraryPanel({ objectId }: { objectId: string }) {
  const items = useLibraryStore((s) => s.items);
  const fetchAll = useLibraryStore((s) => s.fetchAll);
  const deleteItems = useLibraryStore((s) => s.deleteItems);
  const renameItem = useLibraryStore((s) => s.renameItem);

  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [search, setSearch] = useState("");
  const saveInputRef = useRef<HTMLInputElement>(null);

  // Confirm-apply popover state for default poses
  const [confirmApplyDefault, setConfirmApplyDefault] = useState<string | null>(
    null,
  );
  const confirmApplyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Filter default poses by search
  const filteredDefaults = useMemo(() => {
    if (!search) return DEFAULT_POSES;
    const q = search.toLowerCase();
    return DEFAULT_POSES.filter((p) => p.name.toLowerCase().includes(q));
  }, [search]);

  const poseItems = useMemo(() => {
    let filtered = items.filter((i) => i.type === "pose");
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter((i) => i.name.toLowerCase().includes(q));
    }
    return filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [items, search]);

  const handleSave = useCallback(async () => {
    const name = newName.trim() || `Pose ${new Date().toLocaleDateString()}`;
    setSaving(true);
    await savePoseToLibrary(objectId, name);
    setNewName("");
    setShowSaveInput(false);
    setSaving(false);
    fetchAll();
  }, [objectId, newName, fetchAll]);

  const handleApply = useCallback(
    async (fileUrl: string) => {
      await loadPoseFromLibrary(fileUrl, objectId);
    },
    [objectId],
  );

  const handleApplyDefault = useCallback(
    async (pose: DefaultPose) => {
      await applyDefaultPose(pose, objectId);
    },
    [objectId],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = await showConfirmDialog(
        "Delete this pose? This cannot be undone.",
      );
      if (confirmed) await deleteItems([id]);
    },
    [deleteItems],
  );

  useEffect(() => {
    if (showSaveInput)
      requestAnimationFrame(() => saveInputRef.current?.focus());
  }, [showSaveInput]);

  const totalCount = filteredDefaults.length + poseItems.length;

  const inputStyle = PANEL_INPUT_STYLE;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        overflow: "hidden",
      }}
    >
      {/* Save current pose */}
      {!showSaveInput ? (
        <Button
          onClick={() => setShowSaveInput(true)}
          variant="ghost"
          fullWidth
          leftIcon={<Plus size={14} />}
          style={{
            padding: "8px 10px",
            borderRadius: RADIUS_SM,
            border: `0.5px dashed ${COLOR_BORDER_STRONG}`,
            color: COLOR_TEXT_DIM,
            fontSize: FONT_SIZE_SMALL,
            marginBottom: 6,
            justifyContent: "flex-start",
          }}
        >
          Save Current Pose
        </Button>
      ) : (
        <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
          <input
            ref={saveInputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setShowSaveInput(false);
            }}
            placeholder="Pose name"
            style={inputStyle}
          />
          <IconButton
            onClick={handleSave}
            disabled={saving}
            icon={<Check size={14} />}
            label="Save pose"
            style={{ color: COLOR_ACCENT }}
          />
          <IconButton
            onClick={() => setShowSaveInput(false)}
            icon={<X size={14} />}
            label="Cancel"
            style={{ color: COLOR_TEXT_DIM }}
          />
        </div>
      )}

      {/* Search */}
      {totalCount > 3 && (
        <div style={{ marginBottom: 6 }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search poses…"
          />
        </div>
      )}

      {/* Pose list */}
      <div
        style={{
          maxHeight: 300,
          overflowY: "auto",
          overscrollBehavior: "contain",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Default (built-in) poses */}
        {filteredDefaults.length > 0 && (
          <>
            <div
              style={{
                padding: "4px 4px 2px",
                fontSize: FONT_SIZE_TINY,
                color: COLOR_TEXT_FAINT,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Presets
            </div>
            {filteredDefaults.map((pose) => {
              const isConfirming = confirmApplyDefault === pose.id;
              return (
                <div
                  key={pose.id}
                  ref={isConfirming ? confirmApplyRef : undefined}
                  onClick={() => {
                    setConfirmApplyDefault(pose.id);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 4px",
                    borderRadius: RADIUS_SM,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: FONT_SIZE_SMALL,
                        color: COLOR_TEXT_SECONDARY,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pose.name}
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZE_TINY,
                        color: COLOR_TEXT_FAINT,
                      }}
                    >
                      {pose.category}
                    </div>
                  </div>
                  {/* Confirm-apply popover for default poses */}
                  {isConfirming && (
                    <Popover
                      open
                      anchorRef={
                        confirmApplyRef as React.RefObject<HTMLElement>
                      }
                      onClose={() => setConfirmApplyDefault(null)}
                      style={{ padding: 8 }}
                    >
                      <Button
                        data-popover-item
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApplyDefault(pose);
                          setConfirmApplyDefault(null);
                        }}
                        variant="ghost"
                        size="sm"
                        leftIcon={<Check size={14} />}
                        style={{
                          padding: "6px 14px",
                          color: COLOR_ACCENT,
                          fontSize: FONT_SIZE_BODY,
                        }}
                      >
                        Apply Pose
                      </Button>
                    </Popover>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* User-created cloud poses */}
        {poseItems.length > 0 && (
          <div
            style={{
              padding: "6px 4px 2px",
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_FAINT,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Saved
          </div>
        )}

        {poseItems.length === 0 && filteredDefaults.length === 0 && (
          <div
            style={{
              padding: "12px 4px",
              textAlign: "center",
              color: COLOR_TEXT_FAINT,
              fontSize: FONT_SIZE_SMALL,
            }}
          >
            {search ? "No matching poses" : "No saved poses yet"}
          </div>
        )}

        {poseItems.map((item) => (
          <LibraryItemRow
            key={item.id}
            id={item.id}
            name={item.name}
            subtitle={new Date(item.createdAt).toLocaleDateString()}
            thumbnailUrl={item.thumbnailUrl}
            applyLabel="Apply Pose"
            onApply={() => item.fileUrl && handleApply(item.fileUrl)}
            onRename={(newName) => renameItem(item.id, newName)}
            onOverwrite={() => overwritePoseInLibrary(item.id, objectId)}
            onDuplicate={() => duplicateLibraryItem(item.id)}
            onDelete={() => handleDelete(item.id)}
          />
        ))}
      </div>
    </div>
  );
}
